import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import ProcessingIndicator from 'components/ProcessingIndicator';
import { ADD_TO_CART, REMOVE_FROM_CART } from 'constants/gtm';
import { addItem, updateCart } from 'stores/actions/cart';
import {
  MODAL_SELECT_STORE,
  setCurrentModal,
} from 'stores/reducers/store-selection-modal';
import formatStoreName from 'utils/format-store-name';
import { sendEcommerceEvent } from 'utils/metrics';
import {
  surfsideAddProduct,
  surfsideRemoveProduct,
} from 'utils/surfside-analytics';
import toDollars from 'utils/to-dollars';
import styles from './AddToBagButton.module.scss';

const AddToBagButton = memo(
  ({
    availableQuantity = 0,
    batchSKU,
    brand,
    cartQuantity = 0,
    category,
    className,
    currencyCode = 'USD',
    currentPriceCents,
    itemIndex = 0,
    outOfStock = false,
    onError,
    productName,
    quantity = 1,
    regularPriceCents,
    showIncrementButtons = true,
    SKU,
    sourceId,
    sourceName,
    storeClosed,
    storeSelection,
    subcategory,
    variation,
  }) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [atbDropdownOpen, setAtbDropdownOpen] = useState(false);
    const minusText = '-';
    const plusText = '+';
    const buttonStyles = classNames('h-100 w-100 p-0 fs-12 fw-600', styles.cta);
    const sharedCtaContainerStyles = 'col text-center text-primary';
    const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
    const email = useSelector((state) => state.user.userInfo.email);
    const isEnrolled = useSelector(
      (state) => state.user.userInfo.isEnrolledMarketing
    );
    const buttonRef = useRef(null);
    const addToBag = useCallback(async () => {
      const enrolledEmail = isEnrolled ? email : undefined;
      const discountCents = regularPriceCents - currentPriceCents;
      const discountAmount = Number((discountCents / 100).toFixed(2));
      const currentPriceAmount = Number((currentPriceCents / 100).toFixed(2));
      const value = currentPriceAmount * quantity;
      const formattedStoreName = formatStoreName(storeSelection);

      sendEcommerceEvent(ADD_TO_CART, {
        currency: currencyCode,
        email: enrolledEmail,
        value,
        items: [
          {
            /* eslint-disable camelcase */
            discount: discountAmount,
            index: itemIndex,
            item_brand: brand,
            item_category: category,
            item_category2: subcategory,
            item_id: SKU,
            item_list_id: sourceId,
            item_list_name: sourceName,
            item_variant: variation,
            item_name: productName,
            location_id: formattedStoreName,
            price: currentPriceAmount,
            quantity,
            /* eslint-enable camelcase */
          },
        ],
      });

      surfsideAddProduct({
        brand,
        category,
        currency: currencyCode,
        name: productName,
        price: toDollars(currentPriceCents).toFixed(2),
        quantity,
        SKU,
        variation,
      });
      try {
        setIsLoading(true);
        await dispatch(
          addItem({
            batchSKU,
            quantity,
            SKU,
            storeId: storeSelection,
            variation,
            shouldUpdateCartTracker: isLoggedIn,
          })
        );
        if (typeof onError === 'function') {
          onError(false);
        }
      } catch (err) {
        if (typeof onError === 'function') {
          const errorMessage = err?.message ?? '';
          onError(!errorMessage.includes('purchase limit'));
        }
      } finally {
        setIsLoading(false);
      }
    }, [
      batchSKU,
      brand,
      category,
      currencyCode,
      currentPriceCents,
      dispatch,
      email,
      isEnrolled,
      itemIndex,
      onError,
      productName,
      quantity,
      regularPriceCents,
      SKU,
      sourceId,
      sourceName,
      storeSelection,
      subcategory,
      variation,
      isLoggedIn,
    ]);

    const removeFromBag = useCallback(async () => {
      const updatedQuantity = cartQuantity - quantity;
      const discountCents = regularPriceCents - currentPriceCents;
      const discountAmount = Number((discountCents / 100).toFixed(2));
      const currentPriceAmount = Number((currentPriceCents / 100).toFixed(2));
      const value = currentPriceAmount * quantity;
      const formattedStoreName = formatStoreName(storeSelection);

      sendEcommerceEvent(REMOVE_FROM_CART, {
        currency: currencyCode,
        value,
        items: [
          {
            /* eslint-disable camelcase */
            discount: discountAmount,
            index: itemIndex,
            item_brand: brand,
            item_category: category,
            item_category2: subcategory,
            item_id: SKU,
            item_list_id: sourceId,
            item_list_name: sourceName,
            item_variant: variation,
            item_name: productName,
            location_id: formattedStoreName,
            price: currentPriceAmount,
            quantity,
            /* eslint-enable camelcase */
          },
        ],
      });

      surfsideRemoveProduct({
        brand,
        category,
        currency: currencyCode,
        name: productName,
        price: toDollars(currentPriceCents).toFixed(2),
        quantity: 1,
        SKU,
        variation,
      });

      try {
        setIsLoading(true);
        await dispatch(
          updateCart({
            batchSKU,
            quantity: updatedQuantity,
            SKU,
            storeId: storeSelection,
            shouldUpdateCartTracker: isLoggedIn,
          })
        );
        onError(false);
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    }, [
      batchSKU,
      brand,
      category,
      cartQuantity,
      currencyCode,
      currentPriceCents,
      dispatch,
      isLoggedIn,
      itemIndex,
      onError,
      productName,
      quantity,
      regularPriceCents,
      SKU,
      sourceId,
      sourceName,
      subcategory,
      storeSelection,
      variation,
    ]);

    const updateItemQuantity = useCallback(
      async (quantity) => {
        if (quantity === cartQuantity) {
          return;
        }

        const enrolledEmail = isEnrolled ? email : undefined;
        const discountCents = regularPriceCents - currentPriceCents;
        const discountAmount = Number((discountCents / 100).toFixed(2));
        const currentPriceAmount = Number((currentPriceCents / 100).toFixed(2));
        const formattedStoreName = formatStoreName(storeSelection);
        const updatedItem = {
          /* eslint-disable camelcase */
          discount: discountAmount,
          index: itemIndex,
          item_brand: brand,
          item_category: category,
          item_category2: subcategory,
          item_id: SKU,
          item_list_id: sourceId,
          item_list_name: sourceName,
          item_variant: variation,
          item_name: productName,
          location_id: formattedStoreName,
          price: currentPriceAmount,
          quantity,
          /* eslint-enable camelcase */
        };

        const surfsideItem = {
          brand,
          category,
          currency: currencyCode,
          name: productName,
          price: toDollars(currentPriceCents).toFixed(2),
          SKU,
          variation,
        };

        if (quantity < cartQuantity) {
          const quantityRemoved = cartQuantity - quantity;
          const value = quantityRemoved * currentPriceAmount;

          sendEcommerceEvent(REMOVE_FROM_CART, {
            currency: currencyCode,
            value,
            items: [updatedItem],
          });
          surfsideRemoveProduct({
            ...surfsideItem,
            quantity: quantityRemoved,
          });
        } else {
          const quantityAdded = quantity - cartQuantity;
          const value = quantityAdded * currentPriceAmount;

          sendEcommerceEvent(ADD_TO_CART, {
            currency: currencyCode,
            email: enrolledEmail,
            value,
            items: [updatedItem],
          });
          surfsideAddProduct({
            ...surfsideItem,
            quantity: quantityAdded,
          });
        }
        try {
          setIsLoading(true);
          await dispatch(
            updateCart({
              batchSKU,
              quantity,
              SKU,
              storeId: storeSelection,
              shouldUpdateCartTracker: isLoggedIn,
            })
          );
          if (typeof onError === 'function') {
            onError(false);
          }
          setIsLoading(false);
        } catch (err) {
          if (typeof onError === 'function') {
            const errorMessage = err?.message ?? '';
            onError(!errorMessage.includes('purchase limit'));
          }
          setIsLoading(false);
        }
      },
      [
        batchSKU,
        brand,
        cartQuantity,
        category,
        currencyCode,
        currentPriceCents,
        dispatch,
        email,
        isEnrolled,
        isLoggedIn,
        itemIndex,
        onError,
        productName,
        regularPriceCents,
        SKU,
        sourceId,
        sourceName,
        storeSelection,
        subcategory,
        variation,
      ]
    );

    const handleShowDropdownClick = useCallback(() => {
      setAtbDropdownOpen((val) => !val);
    }, []);

    const handleUpdateItemQuantity = async (quantity) => {
      await updateItemQuantity(quantity);
      handleShowDropdownClick();
    };

    const betweenButtonsClassNames = [
      'd-flex align-items-center justify-content-center',
      'bg-neutral-grey',
      'text-center p-3 col-3',
    ];

    useEffect(() => {
      const handleClickEvent = (event) => {
        if (
          buttonRef.current &&
          !buttonRef.current.contains(event.target) &&
          atbDropdownOpen
        ) {
          handleShowDropdownClick();
        }
      };

      window.addEventListener('click', handleClickEvent);

      return () => {
        window.removeEventListener('click', handleClickEvent);
      };
    }, [buttonRef, atbDropdownOpen, handleShowDropdownClick]);

    return cartQuantity === 0 || !showIncrementButtons || storeClosed ? (
      isLoading ? (
        <ProcessingIndicator
          customCss={classNames('bg-primary', className)}
          theme="white"
        />
      ) : cartQuantity === 0 || storeClosed ? (
        <Button
          aria-label={`Add ${productName} to bag`}
          className={classNames('font-sofia fw-700 text-uppercase', className)}
          data-value="add-to-bag-button"
          disabled={(storeSelection && outOfStock) || storeClosed}
          type="button"
          variant="primary"
          onClick={
            storeSelection && !storeClosed
              ? addToBag
              : () => dispatch(setCurrentModal(MODAL_SELECT_STORE))
          }
        >
          {storeClosed
            ? 'Store Closed'
            : storeSelection && outOfStock
            ? 'Out of Stock'
            : storeSelection
            ? 'Add to Bag'
            : 'Select a Store'}
        </Button>
      ) : (
        <div ref={buttonRef} className="d-flex flex-column w-100">
          <Button
            aria-label={`Add more ${productName} to bag`}
            className={classNames(
              'font-sofia fw-700 text-uppercase w-100',
              className
            )}
            data-value="add-multiple-items-to-bag-button"
            type="button"
            variant="primary"
            onClick={handleShowDropdownClick}
          >
            {`${cartQuantity} in Bag`}
            <span
              className={classNames(
                'ms-3 position-relative',
                styles.icon_container
              )}
            >
              <FontAwesomeIcon
                className={classNames('text-white', styles.icon, {
                  [styles.dropdown_open]: atbDropdownOpen,
                })}
                icon={faChevronDown}
              />
            </span>
          </Button>
          {atbDropdownOpen && (
            <div className=" w-100 position-relative">
              <ul
                className={classNames(
                  'list-unstyled mb-0 border border-2 border-primary position-absolute w-100',
                  styles.dropdown_container
                )}
              >
                {[...Array(availableQuantity).keys()].map((val, idx) => {
                  return (
                    <li
                      key={val}
                      className="border-bottom border-bottom-2"
                      value={idx + 1}
                    >
                      <Button
                        type="button"
                        variant="none"
                        className={classNames('btn text-primary p-2 w-100', {
                          'bg-secondary fw-600': idx + 1 === cartQuantity,
                        })}
                        onClick={() => handleUpdateItemQuantity(idx + 1)}
                      >
                        {`${idx + 1}`}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )
    ) : (
      <div
        className={classNames(
          'd-flex justify-content-center font-sofia fw-700 text-primary text-primary border border-0 border-primary w-100',
          className
        )}
      >
        <div className={classNames(sharedCtaContainerStyles, styles.minus_cta)}>
          <Button
            type="button"
            variant="primary"
            title={`Remove one ${productName} from bag`}
            className={buttonStyles}
            disabled={isLoading}
            onClick={removeFromBag}
          >
            {minusText}
          </Button>
        </div>
        {isLoading ? (
          <ProcessingIndicator customCss="bg-neutral-grey" />
        ) : (
          <div className={classNames(betweenButtonsClassNames)}>
            {cartQuantity}
          </div>
        )}
        <div className={classNames(sharedCtaContainerStyles, styles.plus_cta)}>
          <Button
            type="button"
            disabled={availableQuantity === cartQuantity || isLoading}
            variant="primary"
            title={`Add one ${productName} to bag`}
            className={buttonStyles}
            onClick={addToBag}
          >
            {plusText}
          </Button>
        </div>
      </div>
    );
  }
);

export default AddToBagButton;
